/**
 * Max Buy Price Card
 *
 * Calculator for maximum buying price based on:
 * - Expected selling price (user input)
 * - Target margin % (user input, default 10%)
 * - Rest-BPM (from calculation)
 */

import { useTranslation } from 'react-i18next';
import { Calculator, Info } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { calculateMaxBuyPriceBreakdown } from '../lib/calculateMaxBuyPrice';
import { formatEuro } from '../lib/format';
import { cn } from '@/lib/utils';

interface MaxBuyPriceCardProps {
  expectedSellingPrice: number | null;
  targetMarginPct: number;
  restBpm: number | null;
  maxBuyPrice: number | null;
  onSellingPriceChange: (price: number | null) => void;
  onMarginChange: (pct: number) => void;
  className?: string;
}

export function MaxBuyPriceCard({
  expectedSellingPrice,
  targetMarginPct,
  restBpm,
  maxBuyPrice,
  onSellingPriceChange,
  onMarginChange,
  className,
}: MaxBuyPriceCardProps) {
  const { t } = useTranslation('common');

  const hasBpm = restBpm !== null;
  const hasSellingPrice = expectedSellingPrice !== null && expectedSellingPrice > 0;

  // Calculate breakdown for display
  const breakdown =
    hasSellingPrice && hasBpm
      ? calculateMaxBuyPriceBreakdown(expectedSellingPrice, targetMarginPct, restBpm ?? 0)
      : null;

  const handleSellingPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value === '') {
      onSellingPriceChange(null);
    } else {
      onSellingPriceChange(parseInt(value, 10));
    }
  };

  const handleMarginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      onMarginChange(Math.max(0, Math.min(100, value)));
    }
  };

  return (
    <div className={cn('rounded-lg border bg-card p-4', className)}>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-500/10">
          <Calculator className="h-4 w-4 text-green-500" />
        </div>
        <h3 className="font-semibold text-foreground">
          {t('calculator.maxBuyPriceTitle', 'Max Inkoopprijs')}
        </h3>
      </div>

      {/* Inputs */}
      <div className="space-y-3 mb-4">
        {/* Selling price input */}
        <div className="space-y-1.5">
          <Label htmlFor="selling-price" className="text-sm">
            {t('calculator.expectedSellingPrice', 'Verwachte verkoopprijs')}
          </Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              €
            </span>
            <Input
              id="selling-price"
              type="text"
              inputMode="numeric"
              value={expectedSellingPrice ?? ''}
              onChange={handleSellingPriceChange}
              placeholder="25000"
              className="pl-7"
            />
          </div>
        </div>

        {/* Margin input */}
        <div className="space-y-1.5">
          <Label htmlFor="margin-pct" className="text-sm">
            {t('calculator.targetMargin', 'Doelmarge')}
          </Label>
          <div className="relative">
            <Input
              id="margin-pct"
              type="number"
              min={0}
              max={100}
              step={1}
              value={targetMarginPct}
              onChange={handleMarginChange}
              className="pr-7"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              %
            </span>
          </div>
        </div>
      </div>

      {/* Result */}
      {!hasBpm ? (
        <div className="flex items-start gap-2 p-2 rounded-md bg-muted/50 text-sm text-muted-foreground">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <p>
            {t(
              'calculator.needBpmForMaxPrice',
              'Vul een kenteken in om de max inkoopprijs te berekenen'
            )}
          </p>
        </div>
      ) : !hasSellingPrice ? (
        <div className="flex items-start gap-2 p-2 rounded-md bg-muted/50 text-sm text-muted-foreground">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <p>
            {t(
              'calculator.needSellingPrice',
              'Vul een verkoopprijs in om de max inkoopprijs te berekenen'
            )}
          </p>
        </div>
      ) : breakdown ? (
        <div className="space-y-2">
          {/* Breakdown */}
          <div className="text-sm space-y-1 text-muted-foreground">
            <div className="flex justify-between">
              <span>{t('calculator.sellingPrice', 'Verkoopprijs')}</span>
              <span>{formatEuro(breakdown.expectedSellingPrice)}</span>
            </div>
            <div className="flex justify-between">
              <span>
                {t('calculator.margin', 'Marge')} ({breakdown.marginPct}%)
              </span>
              <span>- {formatEuro(breakdown.marginAmount)}</span>
            </div>
            <div className="flex justify-between">
              <span>{t('calculator.restBpm', 'Rest-BPM')}</span>
              <span>- {formatEuro(breakdown.restBpm)}</span>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-border my-2" />

          {/* Max buy price - highlighted */}
          <div className="flex justify-between items-center">
            <span className="font-medium text-foreground">
              {t('calculator.maxBuyPrice', 'Max inkoopprijs')}
            </span>
            <span className="font-semibold text-xl text-green-600 dark:text-green-400">
              {formatEuro(breakdown.maxBuyPrice)}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
